import React, { useEffect, useReducer, useState } from 'react';
import {
  useHistory, useParams, useLocation, Redirect,
} from 'react-router-dom';
import {
  Box, Container, styled, ThemeProvider,
} from '@material-ui/core';
import { usePostHog } from 'posthog-js/react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useQuery } from '@apollo/client';

import { isValidPhoneNumber } from 'react-phone-number-input';

import reducer, { initialState } from './preOnboardingState';
import { LanguageSelector } from '../Onboarding/components/LanguageSelector/LanguageSelector';
import {
  CheckYourEmail, SubmitEmail, ErrorState, CoverageCheckWithEmail,
} from './steps';
import Styled from './PreOnboardingPage.styles';
import { WhoAreYouEnrollingActivate } from './steps/WhoAreYouEnrollingActivate';

import { Footer } from '../Onboarding/components/Footer/Footer';

import { ConsentWidget } from '../Onboarding/components/ConsentWidget/ConsentWidget';

import { SleepioWellnessOnboardingParams } from '../SleepioWellnessOnboarding/SleepioWellnessOnboardingPage';
import CoverageRejection from '../SleepioWellnessOnboarding/steps/CoverageRejection';
import CreatePassword from '../SleepioWellnessOnboarding/steps/CreatePassword/CreatePassword';
import {
  ReadyToStartSleepioWellness,
} from '../SleepioWellnessOnboarding/steps/ReadyToStartSleepioWellness/ReadyToStartSleepioWellness';
import { EligibilityFormCustomization } from '../Onboarding/OnboardingFlowContext';

import SleepioLogoSrc from '@/assets/SleepioLogoIcon.svg';
import SparkDirectLogo from '@/assets/SparkDirectLogo.svg';
import theme from '@/themes/sleepioTheme';
import sparkTheme from '@/themes/sparkTheme';
import { useConsent } from '@/hooks/useConsent';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { getProgram } from '@/utils/posthog';
import { EMAIL_VALIDATION_REGEX, PASSWORD_VALIDATION_REGEX, PHONE_VALIDATION_REGEX } from '@/utils/constants';
import { isBlockedEmailDomain } from '@/utils/stringValidators';
import {
  getPdtUid, Product, isSpark as isSparkHelper,
} from '@/utils/productHelpers';
import { ELIGIBILITY_CONFIGURATION_QUERY } from '@/apollo/queries';
import { buildMobileRedirectUrl } from '@/utils/urlHelpers';
import Loader from '@/pages/SleepioWellnessOnboarding/components/Loader';
import { ActiveFeatureFlag } from '@/hooks/usePostHogFeatureFlags';
import { logError } from '@/utils/logError';
import {
  applyCoverageFieldsRefinement, buildEmployeeIdErrorMessage, createCoverageFieldsSchema,
} from '@/components/CoverageFields/coverageFieldsSchema';

export type PreOnboardingParams = {
  slug: string;
  product: Product;
};

const ContentContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: '100vh',
});

const emailSchema = z.string().nonempty({ message: 'coverage.errors.email_error' }).transform((v) => v.trim())
  .refine((v) => EMAIL_VALIDATION_REGEX.test(v), { message: 'coverage.errors.email_error' })
  .refine((v) => !isBlockedEmailDomain(v), { message: 'coverage.errors.blocked_domain_error' });

const schema = z.object({
  email: emailSchema,
  phoneNumber: z
    .union([z.literal(''), z.string().regex(PHONE_VALIDATION_REGEX)])
    .optional(),
  termsAndPrivacy: z.boolean().refine((value) => value === true),
  agreeToReceiveTextMessages: z.boolean().optional(),
  marketingConsent: z.boolean().optional(),
});

const sleepioVariantFieldsSchema = createCoverageFieldsSchema({
  firstName: 'coverage.errors.first_name_error',
  lastName: 'coverage.errors.last_name_error',
  zipCode: 'coverage.errors.zipcode_error',
  dateOfBirth: 'coverage.errors.dob_error',
  employeeId: 'coverage.errors.employee_id_error',
});

export type SetupForm = z.infer<typeof schema> & Partial<z.infer<typeof sleepioVariantFieldsSchema>> & {
  password?: string;
  confirmPassword?: string;
};

// Exported so CoverageCheckWithEmail's own tests can wire in this exact schema, not a hand-copy.
export const buildSleepioVariantCoverageSchema = (
  formCustomization: EligibilityFormCustomization,
  t: (key: string) => string,
) => applyCoverageFieldsRefinement(schema.merge(sleepioVariantFieldsSchema), formCustomization, {
  zipCode: 'coverage.errors.zipcode_error',
  employeeId: buildEmployeeIdErrorMessage(formCustomization, t),
});

const PreOnboardingPage = () => {
  useAcceptedLanguage();
  useConsent();
  const { t } = useTranslation();
  const posthog = usePostHog();
  const { slug } = useParams<SleepioWellnessOnboardingParams>();
  const { data, loading } = useQuery(ELIGIBILITY_CONFIGURATION_QUERY,
    { variables: { organizationSlug: slug, pdtUid: getPdtUid('sleepio') } });
  const [state, dispatch] = useReducer(reducer, initialState);
  const { step } = state;
  const { product } = useParams<PreOnboardingParams>();
  const history = useHistory();
  // Set once CoverageCheckWithEmail confirms eligibility, so CreatePassword can render
  // in place - no navigation, so nothing needs to survive a page change.
  const [eligibleCoverage, setEligibleCoverage] = useState<{ careTeamUid: string; token: string } | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const location = useLocation<{ fromEmployerPath?: boolean } | undefined>();
  const params = new URLSearchParams(location.search);
  const initialAutoSelect = params.get('autoSelect');

  // Bounced back from CreatePassword's email-verification handoff with a completed login.
  const isSleepioBounce = product === 'sleepio';
  // These live in the fragment, not the query string - see CreatePassword.tsx's handoffHash.
  const hashParams = new URLSearchParams(location.hash.slice(1));
  const bounceToken = isSleepioBounce ? hashParams.get('token') : null;
  const bounceRedirectUri = isSleepioBounce ? params.get('redirect_uri') : null;
  // TODO: replace with a single-use exchange token from Cortex - this one can be replayed indefinitely.
  // replaceState() doesn't notify react-router, so ErrorState alone would leave the loader stuck forever.
  // Failure-only: success keeps the loader up on purpose while the app-switch is still pending.
  const [bounceFailed, setBounceFailed] = useState(false);
  useEffect(() => {
    if (!bounceToken || !bounceRedirectUri) {
      return undefined;
    }
    const url = buildMobileRedirectUrl(bounceRedirectUri, {
      token: bounceToken,
      refreshToken: hashParams.get('refresh_token') || undefined,
      userId: hashParams.get('user_id') || undefined,
      userEmail: hashParams.get('user_email') || undefined,
    });
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (url) {
      window.location.href = url;
      // No signal exists for whether a custom-scheme deep link actually opened - assume failure after a wait.
      timeoutId = setTimeout(() => {
        dispatch({ type: 'SET_STEP', step: 'ErrorState' });
        setBounceFailed(true);
      }, 2000);
    } else {
      logError('[PreOnboardingPage]', 'bounce-back redirect_uri failed validation');
      dispatch({ type: 'SET_STEP', step: 'ErrorState' });
      setBounceFailed(true);
    }
    // Scrub the tokens from the address bar/history now that they've been consumed.
    window.history.replaceState(null, '', location.pathname);
    return () => clearTimeout(timeoutId);
  }, [bounceToken, bounceRedirectUri, location.search, location.hash, location.pathname]);

  const isSpark = isSparkHelper(product);
  const [showWho, setShowWho] = useState(isSpark && initialAutoSelect !== 'myself');
  const eligibilityConfig = data?.eligibilityConfiguration;
  // Same field mapping SleepioWellnessOnboardingPage.tsx already uses for this exact query.
  const formCustomization: EligibilityFormCustomization = {
    showZipCode: eligibilityConfig?.collectZipcodeForEligibility,
    showEmployeeId: eligibilityConfig?.hasEmployeeId,
    showAddressField: eligibilityConfig?.hasAddressField,
    employeeIdFieldRename: eligibilityConfig?.employeeIdFieldRename,
    isGeneric: eligibilityConfig?.isGeneric,
    employeeIdValidationEnabled: eligibilityConfig?.employeeIdValidationEnabled,
    employeeIdAllowedChars: eligibilityConfig?.employeeIdAllowedChars,
    employeeIdMinLength: eligibilityConfig?.employeeIdMinLength,
    employeeIdMaxLength: eligibilityConfig?.employeeIdMaxLength,
    employeeIdValidationErrorMessage: eligibilityConfig?.employeeIdValidationErrorMessage,
    employeeIdExample: eligibilityConfig?.employeeIdExample,
  };

  // Exact match, not "not Spark" — sleepio-canopy shares the same backend PDT as sleepio.
  const isSleepioProduct = product === 'sleepio';

  // Same invalid-org guard SleepioWellnessOnboardingPage.tsx already has for this exact query.
  // Sleepio-only: the query itself is always scoped to Sleepio's own PDT, so this check
  // isn't meaningful for Spark/Daylight orgs that were never registered under it.
  useEffect(() => {
    if (isSleepioProduct && !loading && eligibilityConfig?.isValid === false) {
      dispatch({ type: 'SET_STEP', step: 'ErrorState' });
    }
  }, [isSleepioProduct, loading, eligibilityConfig]);

  useEffect(() => {
    // Skip on the bounce-back landing (PreOnboardingPage.tsx:138) - it's the same
    // session resuming in a fresh ephemeral browser, not a real new visitor.
    if (bounceToken && bounceRedirectUri) {
      return undefined;
    }
    // Skip when arriving from CheckYourOrganization: the session already started
    // there; a second reset() would fragment the funnel (PAT-1220).
    if (posthog && !location.state?.fromEmployerPath) {
      const start = () => {
        posthog.reset();
        posthog.capture('ActivationFlowStarted', { program: getProgram(product), org_slug: slug });
      };
      if (!isSleepioProduct) {
        start();
        return undefined;
      }
      let hasRun = false;
      const run = () => {
        if (hasRun) {
          return;
        }
        hasRun = true;
        start();
      };
      // Same 2s cap as the flagsReady fallback below, so this never gets silently dropped either.
      const timeoutId = setTimeout(run, 2000);
      // Waits for any flags request already in flight, so reset() never orphans it into a stray response.
      const unsubscribe = posthog.onFeatureFlags(run);
      return () => {
        clearTimeout(timeoutId);
        unsubscribe();
      };
    }
    return undefined;
  }, [posthog, product, getProgram, slug, location.state, isSleepioProduct, bounceToken, bounceRedirectUri]);

  // False only for Sleepio, until PostHog confirms the org-tagged flags actually reloaded
  // (or the 2s cap below gives up and defaults to control).
  const [flagsReady, setFlagsReady] = useState(!isSleepioProduct);
  // Only sleepio can ever see the variant, so only sleepio should count as "exposed" to it.
  const [experimentVariant, setExperimentVariant] = useState<string | boolean | undefined>(undefined);

  useEffect(() => {
    // Skip on the bounce-back landing too - unless it already failed, since "Try again"
    // needs this to actually resolve flagsReady instead of stalling on the Loader forever.
    if (bounceToken && bounceRedirectUri && !bounceFailed) {
      return undefined;
    }
    // Must run after the reset() effect above, which clears the store this writes to.
    if (isSleepioProduct && posthog && slug) {
      // Shared with the timeout below, so whichever settles first locks out the other one too -
      // a real response landing after the cap can't swap the screen out from under the user.
      let hasSettled = false;
      // If flags were already loaded, onFeatureFlags fires immediately with a stale pre-org_slug value - skip it.
      // Once the first real fire lands, ignore any later ones too - a late straggler can't overwrite it.
      const settle = () => {
        let skipStaleFire = posthog.featureFlags?.hasLoadedFlags;
        posthog.setPersonPropertiesForFlags({ org_slug: slug }, true);
        return posthog.onFeatureFlags(() => {
          if (skipStaleFire) {
            skipStaleFire = false;
            return;
          }
          if (hasSettled) {
            return;
          }
          hasSettled = true;
          setFlagsReady(true);
          setExperimentVariant(posthog.getFeatureFlag(ActiveFeatureFlag.CAP_REMOVE_EMAIL_VERIFICATION_STEP_EXPERIMENT));
        });
      };
      // Same 2s cap ActivationComplete.tsx/SparkEmailCallback.tsx already use for a PostHog wait.
      const timeoutId = setTimeout(() => {
        if (hasSettled) {
          return;
        }
        hasSettled = true;
        setFlagsReady(true);
      }, 2000);
      if (posthog.featureFlags?.hasLoadedFlags) {
        const unsubscribe = settle();
        return () => {
          clearTimeout(timeoutId);
          unsubscribe();
        };
      }
      // Cold load: guarantees a load is happening, since reset() may have left nothing in flight.
      posthog.featureFlags?.ensureFlagsLoaded();
      let initialSettled = false;
      let cleanup: (() => void) | undefined;
      const unsubscribeInitial = posthog.onFeatureFlags(() => {
        if (initialSettled) {
          return;
        }
        initialSettled = true;
        cleanup = settle();
      });
      return () => {
        clearTimeout(timeoutId);
        unsubscribeInitial();
        cleanup?.();
      };
    }
    return undefined;
  }, [isSleepioProduct, posthog, slug, bounceToken, bounceRedirectUri, bounceFailed]);

  const isSleepioVariant = isSleepioProduct && experimentVariant === 'variant';

  // CreatePassword only renders password/confirmPassword, so swap in a schema that validates them.
  const schemaForProduct = () => {
    if (step === 'CreatePassword') {
      return z.object({
        password: z.string({ required_error: t('coverage.errors.password_rules_error') })
          .regex(PASSWORD_VALIDATION_REGEX, { message: t('coverage.errors.password_rules_error') }),
        confirmPassword: z.string({ required_error: t('coverage.errors.confirm_password_error') }),
      }).refine((values) => values.password === values.confirmPassword, {
        message: t('coverage.errors.confirm_password_error'),
        path: ['confirmPassword'],
      });
    }
    if (isSpark) {
      return z.object({
        email: emailSchema,
        phoneNumber: z.string().refine(
          (v) => isValidPhoneNumber(v || ''),
          { message: 'Please enter a valid phone number' },
        ),
        termsAndPrivacy: z.boolean().refine((value) => value === true),
        agreeToReceiveTextMessages: z.boolean().refine((v) => v === true),
        marketingConsent: z.boolean().optional(),
      });
    }
    if (isSleepioVariant) {
      return buildSleepioVariantCoverageSchema(formCustomization, t);
    }
    return schema;
  };

  const methods = useForm<SetupForm>({
    resolver: zodResolver(schemaForProduct()),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      email: '',
      phoneNumber: '',
      termsAndPrivacy: false,
      agreeToReceiveTextMessages: false,
      marketingConsent: false,
      firstName: '',
      lastName: '',
      zipCode: '',
      employeeId: '',
      dateOfBirth: '',
      password: '',
      confirmPassword: '',
    },
  });

  const renderStep = () => {
    if (isSpark && showWho) {
      return (
        <ThemeProvider theme={sparkTheme}>
          <WhoAreYouEnrollingActivate
            onMyself={() => setShowWho(false)}
            onDependent={() => {
              setShowWho(false);
              history.replace(`/coverage/${product}/${slug}?autoSelect=dependent`);
            }}
          />
        </ThemeProvider>
      );
    }
    switch (step) {
    case 'SubmitEmail':
      if (loading || !flagsReady) {
        return <Loader />;
      }
      if (isSleepioVariant) {
        return (
          <CoverageCheckWithEmail
            formCustomization={formCustomization}
            onEligible={(careTeamUid, token) => {
              setEligibleCoverage({ careTeamUid, token });
              dispatch({ type: 'SET_STEP', step: 'CreatePassword' });
            }}
            onExistingUser={() => dispatch({ type: 'SET_STEP', step: 'CheckYourEmail' })}
            onIneligible={() => dispatch({ type: 'SET_STEP', step: 'CoverageRejection' })}
            onError={() => dispatch({ type: 'SET_STEP', step: 'ErrorState' })}
          />
        );
      }
      return (
        <SubmitEmail
          onNext={() => dispatch({ type: 'SET_STEP', step: 'CheckYourEmail' })}
          onError={() => dispatch({ type: 'SET_STEP', step: 'ErrorState' })}
          isGeneric={data?.eligibilityConfiguration?.isGeneric}
        />
      );
    case 'CheckYourEmail':
      if (loading) {
        return <Loader />;
      }
      return <CheckYourEmail isGeneric={data?.eligibilityConfiguration?.isGeneric} />;
    case 'CoverageRejection':
      // Skips window.location.reload()'s default, which would re-roll this person's experiment arm.
      return <CoverageRejection onTryAgain={() => dispatch({ type: 'SET_STEP', step: 'SubmitEmail' })} />;
    case 'CreatePassword':
      if (!eligibleCoverage) {
        return null;
      }
      return (
        <CreatePassword
          careTeamUid={eligibleCoverage.careTeamUid}
          token={eligibleCoverage.token}
          product={product}
          onNext={(newAuthToken: string) => {
            setAuthToken(newAuthToken);
            dispatch({ type: 'SET_STEP', step: 'ReadyToStartSleepioWellness' });
          }}
          onError={() => dispatch({ type: 'SET_STEP', step: 'ErrorState' })}
          isGeneric={formCustomization.isGeneric}
          bounceThroughEmailVerification
        />
      );
    case 'ReadyToStartSleepioWellness':
      if (!authToken || !eligibleCoverage) {
        return null;
      }
      return (
        <ReadyToStartSleepioWellness
          authToken={authToken}
          careTeamUid={eligibleCoverage.careTeamUid}
          product={product}
        />
      );
    case 'ErrorState':
      return <ErrorState onNext={() => dispatch({ type: 'SET_STEP', step: 'SubmitEmail' })} />;
    default:
      return null;
    }
  };

  if (bounceToken && bounceRedirectUri && step !== 'ErrorState' && !bounceFailed) {
    return <Loader />;
  }

  if (isSpark && initialAutoSelect === 'myself') {
    const qs = new URLSearchParams(location.search);
    qs.delete('autoSelect');
    const clean = qs.toString();
    return (
      <Redirect
        to={{
          pathname: location.pathname,
          search: clean ? `?${clean}` : '',
          hash: location.hash,
        }}
      />
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <ContentContainer>
        <Styled.Header
          style={isSpark
            ? {
              paddingTop: 40,
              paddingBottom: 0,
            } : undefined}
        >
          <Styled.SleepioHeader
            style={isSpark
              ? {
                alignItems: 'center',
                height: 48,
              } : undefined}
          >
            {!isSpark ? (
              <img src={SleepioLogoSrc} height="48px" alt="sleepio logo" />
            ) : (
              <img
                src={SparkDirectLogo}
                alt="spark direct logo"
                height={39.5}
                width={180}
                style={{ display: 'block' }}
              />
            )}
          </Styled.SleepioHeader>
          <LanguageSelector iconOnly={isSpark} />
        </Styled.Header>
        <Container
          maxWidth={isSpark && showWho ? 'sm' : 'xs'}
          style={{ flex: 1 }}
        >
          <FormProvider {...methods}>{renderStep()}</FormProvider>
          <ConsentWidget />
        </Container>
        <Footer />
      </ContentContainer>
    </ThemeProvider>
  );
};

export default PreOnboardingPage;
